import React, { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  AlertTitle,
  Avatar,
  Box,
  Button,
  Chip,
  Skeleton,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Delete as DeleteIcon,
  Place as PlaceIcon,
  Send as SendIcon,
  VisibilityOff as HiddenIcon,
} from '@mui/icons-material';
import PageLayout from '../../components/layout/PageLayout';
import ReportDialog from '../../components/community/ReportDialog';
import ModerateDialog from '../../components/community/ModerateDialog';
import { apiCall } from '../../utils/fetchAPI';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { Loading, PanelSkeleton } from '../../components/layout/Skeletons';
import { translateApiMessage } from '../../utils/apiMessages';
import {
  POST_STATUS,
  authorInitials,
  formatRelativeDate,
  moderationReason,
  postTypeSx,
} from '../../constants/community';
import ModeratorBadge from '../../components/community/ModeratorBadge';
import PhotoGallery from '../../components/community/PhotoGallery';

function CommentForm({ postUuid, onAdded }) {
  const { isAuthenticated } = useAuth();
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  if (!isAuthenticated) {
    return (
      <Alert severity="info">
        <RouterLink to="/login">Connectez-vous</RouterLink> pour répondre.
      </Alert>
    );
  }

  const submit = async (event) => {
    event.preventDefault();
    setSending(true);
    setError('');

    try {
      const response = await apiCall(`/posts/${postUuid}/comments`, {
        method: 'POST',
        body: JSON.stringify({ body: body.trim() }),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        setError(translateApiMessage(data.error) || 'Votre réponse n’a pas pu être envoyée.');
        return;
      }

      setBody('');
      onAdded(data.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <Box component="form" onSubmit={submit}>
      <TextField
        label="Votre réponse"
        value={body}
        onChange={(e) => setBody(e.target.value)}
        multiline
        minRows={3}
        fullWidth
        required
        disabled={sending}
        inputProps={{ maxLength: 2000 }}
      />

      {error && (
        <Alert severity="error" sx={{ mt: 1.5 }}>
          {error}
        </Alert>
      )}

      <Button type="submit" variant="contained" size="large" startIcon={<SendIcon />} disabled={sending} sx={{ mt: 2 }}>
        Répondre
      </Button>
    </Box>
  );
}

/**
 * One post and its thread.
 *
 * Comments are flat: a neighbourhood question gets answers, not a debate tree.
 */
function PostPage() {
  const { uuid } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { user, isAdmin } = useAuth();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);

    try {
      const response = await apiCall(`/posts/${uuid}`, { method: 'GET' });

      if (404 === response.status) {
        setNotFound(true);
        return;
      }

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(translateApiMessage(data.error) || 'Cette publication n’a pas pu être chargée.');
      }

      setPost(data.data);
      setError('');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [uuid]);

  useEffect(() => {
    load();
  }, [load]);

  const removePost = async () => {
    const response = await apiCall(`/posts/${uuid}`, { method: 'DELETE' });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      toast.error(translateApiMessage(data.error) || 'La publication n’a pas pu être supprimée.');
      return;
    }

    toast.success('Publication supprimée.');
    navigate('/quartier');
  };

  const removeComment = async (commentUuid) => {
    const response = await apiCall(`/comments/${commentUuid}`, { method: 'DELETE' });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      toast.error(translateApiMessage(data.error) || 'Le commentaire n’a pas pu être supprimé.');
      return;
    }

    load();
  };

  if (loading) {
    return (
      <PageLayout centered={false}>
        <Loading label="Chargement de la publication">
          <Box sx={{ width: '100%' }}>
            <Skeleton variant="rectangular" width={200} height={44} sx={{ borderRadius: 999, mb: 2.5 }} />
            <Paper variant="outlined" sx={{ p: { xs: 2.5, md: 4 }, mb: 3 }}>
              <Skeleton variant="rectangular" width={120} height={30} sx={{ borderRadius: 999, mb: 2 }} />
              <Skeleton variant="text" width="70%" height={44} sx={{ mb: 2 }} />
              <Stack direction="row" sx={{ alignItems: 'center', gap: 1.5, mb: 3 }}>
                <Skeleton variant="circular" width={42} height={42} />
                <Skeleton variant="text" width={160} height={22} />
              </Stack>
              <Skeleton variant="text" height={22} />
              <Skeleton variant="text" height={22} />
              <Skeleton variant="text" width="60%" height={22} />
            </Paper>
            <PanelSkeleton lines={3} />
          </Box>
        </Loading>
      </PageLayout>
    );
  }

  if (notFound) {
    return (
      <PageLayout>
        <Paper variant="outlined" sx={{ p: 5, textAlign: 'center', maxWidth: 520 }}>
          <Typography variant="h3" component="h1" sx={{ mb: 1 }}>
            Cette publication n’existe plus
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            Elle a été supprimée par son auteur ou retirée par la modération.
          </Typography>
          <Button component={RouterLink} to="/quartier" variant="contained">
            Retour au fil
          </Button>
        </Paper>
      </PageLayout>
    );
  }

  if (error) {
    return (
      <PageLayout>
        <Alert severity="error">{error}</Alert>
      </PageLayout>
    );
  }

  const isAuthor = user?.username && user.username === post.author_username;

  const status = POST_STATUS[post.status];
  const isLive = 'PUBLISHED' === post.status;
  const reason = moderationReason(post);

  return (
    <PageLayout centered={false}>
      <Box sx={{ width: '100%' }}>
        <Button component={RouterLink} to="/quartier" startIcon={<ArrowBackIcon />} sx={{ mb: 2.5 }}>
          Le fil du quartier
        </Button>

        <Paper variant="outlined" sx={{ p: { xs: 2.5, md: 4 }, mb: 3 }}>
          {/* A post taken down says so first, with why: this is the screen its
              author opens to find out what happened to it. */}
          {!isLive && status?.notice && (
            <Alert
              severity="warning"
              icon={<HiddenIcon />}
              sx={{ mb: 2.5 }}
            >
              <AlertTitle>{status.label}</AlertTitle>
              {status.notice}
              {reason && (
                <Box component="span" sx={{ display: 'block', mt: 0.5, fontStyle: 'italic' }}>
                  Motif&nbsp;: {reason}
                </Box>
              )}
            </Alert>
          )}

          <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1, mb: 2 }}>
            <Chip label={post.type_label} size="small" sx={postTypeSx(post.type)} />
            {post.neighborhood && (
              <Chip icon={<PlaceIcon />} label={post.neighborhood.name} size="small" />
            )}
          </Stack>

          <Typography variant="h1" component="h1" sx={{ fontSize: { xs: '1.75rem', md: '2.25rem' }, mb: 2 }}>
            {post.title}
          </Typography>

          <Stack direction="row" sx={{ alignItems: 'center', gap: 1.5, mb: 3 }}>
            <Avatar
              aria-hidden="true"
              sx={{ width: 42, height: 42, bgcolor: 'success.light', color: 'secondary.dark' }}
            >
              {authorInitials(post.author)}
            </Avatar>
            <Box>
              <Typography variant="body1" sx={{ fontWeight: 700 }}>
                {post.author}
                <ModeratorBadge show={post.author_is_moderator} />
              </Typography>
              <Typography variant="caption">
                {formatRelativeDate(post.created_at)}
                {post.updated_at ? ' · modifié' : ''}
              </Typography>
            </Box>
          </Stack>

          <Typography variant="body1" sx={{ whiteSpace: 'pre-line', fontSize: '1.09375rem' }}>
            {post.body}
          </Typography>

          {post.medias?.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <PhotoGallery medias={post.medias} title={post.title} />
            </Box>
          )}

          <Stack
            direction="row"
            sx={{
              alignItems: 'center',
              gap: 1,
              flexWrap: 'wrap',
              mt: 3,
              pt: 2.5,
              borderTop: '1px solid',
              borderColor: 'surface.rule',
            }}
          >
            {(isAuthor || isAdmin) && (
              <Button size="small" color="error" variant="outlined" startIcon={<DeleteIcon />} onClick={removePost}>
                Supprimer
              </Button>
            )}
            <Box sx={{ flexGrow: 1 }} />
            {/* Once hidden, the post is gone for everybody but its author:
                back to the feed rather than a "not found" on this page. */}
            <ModerateDialog
              targetType="POST"
              targetUuid={post.uuid}
              citySlug={post.city_slug}
              isOwn={Boolean(isAuthor)}
              hidden={!isLive}
              onModerated={() => navigate('/quartier')}
            />
            {!isAuthor && <ReportDialog targetType="POST" targetUuid={post.uuid} />}
          </Stack>
        </Paper>

        <Typography variant="h2" component="h2" sx={{ mb: 2.5 }}>
          {post.comment_count} réponse{1 === post.comment_count ? '' : 's'}
        </Typography>

        <Stack spacing={2}>
          {(post.comments || []).map((comment) => {
            const mine = user?.username && user.username === comment.author_username;

            return (
              <Paper key={comment.uuid} variant="outlined" sx={{ p: { xs: 2.5, md: 3 } }}>
                <Stack direction="row" sx={{ alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                  <Avatar
                    aria-hidden="true"
                    sx={{ width: 38, height: 38, fontSize: '0.90625rem', bgcolor: 'surface.chip', color: 'text.secondary' }}
                  >
                    {authorInitials(comment.author)}
                  </Avatar>
                  <Typography variant="body2">
                    <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
                      {comment.author}
                    </Box>
                    <ModeratorBadge show={comment.author_is_moderator} />
                    {` · ${formatRelativeDate(comment.created_at)}`}
                  </Typography>
                </Stack>

                <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
                  {comment.body}
                </Typography>

                <Stack direction="row" sx={{ gap: 1, mt: 1.5, alignItems: 'center' }}>
                  {(mine || isAdmin) && (
                    <Button size="small" color="error" variant="outlined" onClick={() => removeComment(comment.uuid)}>
                      Supprimer
                    </Button>
                  )}
                  <Box sx={{ flexGrow: 1 }} />
                  <ModerateDialog
                    targetType="COMMENT"
                    targetUuid={comment.uuid}
                    citySlug={post.city_slug}
                    isOwn={Boolean(mine)}
                    hidden={'HIDDEN' === comment.status}
                    onModerated={load}
                  />
                  {!mine && <ReportDialog targetType="COMMENT" targetUuid={comment.uuid} />}
                </Stack>
              </Paper>
            );
          })}
        </Stack>

        <Paper variant="outlined" sx={{ p: { xs: 2.5, md: 3 }, mt: 3 }}>
          <CommentForm postUuid={post.uuid} onAdded={setPost} />
        </Paper>
      </Box>
    </PageLayout>
  );
}

export default PostPage;
